import { useMemo, Fragment } from 'react';
import { Box, Divider, InputLabel, Typography } from '@mui/material';

import { ScaleRatingFormField } from 'types';
import { isSingleScaleRating } from 'utils/scale-rating';
import { QuestionLabel } from 'components/question-label';
import {
  questionLabelText,
  hasVisibleQuestionLabel,
} from 'utils/question-label';

// Direct imports avoid initializing unrelated fields through the barrel.
import { ScaleRatingField } from '../fields/scale-rating';

const sharedHeaderLabelSx = {
  whiteSpace: 'normal',
  paddingBottom: '16px',
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
} as const;

interface ScaleHeaderProps {
  min: number;
  count: number;
  labels: string[];
  showSimpleHeader: boolean;
  hasAllValueLabels: boolean;
}

const ScaleHeader = ({
  min,
  count,
  labels,
  showSimpleHeader,
  hasAllValueLabels,
}: ScaleHeaderProps) => {
  if (showSimpleHeader && !hasAllValueLabels) {
    return (
      <div className="mb-1 flex w-full items-center justify-between">
        <Typography component="div" variant="body1" sx={sharedHeaderLabelSx}>
          <QuestionLabel value={labels[0]} />
        </Typography>
        <Typography component="div" variant="body1" sx={sharedHeaderLabelSx}>
          <QuestionLabel value={labels[count - 1]} />
        </Typography>
      </div>
    );
  }

  if (hasAllValueLabels) {
    return (
      <div className="mb-1 flex w-full items-end">
        {labels.map((labelText, valueIndex) => (
          <Typography
            component="div"
            variant="body1"
            key={min + valueIndex}
            sx={sharedHeaderLabelSx}
            className="flex flex-1 justify-center text-center leading-tight break-words"
          >
            <QuestionLabel value={labelText} />
          </Typography>
        ))}
      </div>
    );
  }

  return (
    <div className="mb-1 flex items-end gap-2.5">
      {labels.map((labelText, valueIndex) => (
        <Typography
          component="div"
          variant="body1"
          key={min + valueIndex}
          className="min-h-8 w-8 text-center leading-tight break-words"
          sx={{
            ...sharedHeaderLabelSx,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <QuestionLabel value={labelText} />
        </Typography>
      ))}
    </div>
  );
};

export const FormScaleRatingField = ({
  id,
  label,
  options,
}: ScaleRatingFormField) => {
  const isSingle = isSingleScaleRating(options?.options);
  const visibleQuestionLabel = hasVisibleQuestionLabel(label) ? label : '';
  const min = options?.minValue ?? 1;
  const max = options?.maxValue ?? 7;
  const count = max - min + 1;
  const valueLabels = options?.valueLabels;
  const { labels, showFullHeader, showSimpleHeader, hasAllValueLabels } =
    useMemo(() => {
      const computedLabels = Array.from({ length: count }, (_, i) => {
        const valueLabel = valueLabels?.[i]?.trim() ?? '';
        return hasVisibleQuestionLabel(valueLabel) ? valueLabel : '';
      });
      const hasAnyValueLabel = computedLabels.some(Boolean);
      const hasAllLabels = computedLabels.every(Boolean);
      const hasFirst = !!computedLabels[0];
      const hasLast = !!computedLabels[count - 1];
      const intermediateCount = computedLabels
        .slice(1, Math.max(1, count - 1))
        .filter(Boolean).length;
      const isSimpleHeader = (hasFirst || hasLast) && intermediateCount === 0;

      return {
        labels: computedLabels,
        hasAllValueLabels: hasAllLabels,
        showSimpleHeader: isSimpleHeader,
        showFullHeader: hasAnyValueLabel && !isSimpleHeader,
      };
    }, [count, valueLabels]);
  return (
    <Box className="flex flex-col gap-2">
      {!isSingle && visibleQuestionLabel && (
        <InputLabel
          component="div"
          className="mb-2"
          sx={{ whiteSpace: 'pre-line' }}
        >
          <QuestionLabel value={visibleQuestionLabel} />
        </InputLabel>
      )}
      {options?.options.map((option, index) => (
        <Fragment key={`${id}-${option}-${index}`}>
          <ScaleRatingField
            min={min}
            max={max}
            name={`${id}.${index}`}
            stretchLayout={hasAllValueLabels}
            label={
              isSingle
                ? visibleQuestionLabel
                : hasVisibleQuestionLabel(option)
                  ? option
                  : ''
            }
            ariaLabel={
              [
                questionLabelText(visibleQuestionLabel),
                !isSingle && hasVisibleQuestionLabel(option)
                  ? questionLabelText(option)
                  : '',
              ]
                .filter(Boolean)
                .join(': ') || id
            }
            header={
              index === 0 && (showFullHeader || showSimpleHeader) ? (
                <ScaleHeader
                  min={min}
                  count={count}
                  labels={labels}
                  showSimpleHeader={showSimpleHeader}
                  hasAllValueLabels={hasAllValueLabels}
                />
              ) : undefined
            }
          />
          {index < options.options.length - 1 && <Divider sx={{ my: 2 }} />}
        </Fragment>
      ))}
    </Box>
  );
};
